/**
 * f12.1: "Commitment by client" -- the FS sheet table of `review.html`.
 *
 * Rows are grouped by Business Unit with a subtotal and the Unallocated budget
 * line (Finance only); the sticky total row closes the table. Rows and
 * subtotals are in the unit's currency, the total in the presentation currency
 * with its conversion note (adr-12). Every figure is the server's.
 */

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';

import type { Review, ReviewFigures, ReviewRow, ReviewUnitSection } from '@/api/client';

import { deltaClass, formatDelta, formatMoney, riskClass, signOf } from './reviewFormat';

const FINANCE_ONLY = 'Finance only';

function BudgetCell({ figures, visible }: { figures: ReviewFigures; visible: boolean }) {
  if (!visible) {
    return <td className="num" title={FINANCE_ONLY}>—</td>;
  }
  return <td className="num">{formatMoney(figures.budget)}</td>;
}

function PreviousCell({ figures, label }: { figures: ReviewFigures; label: string | null }) {
  if (!figures.previous_com) {
    return (
      <td className="num" title={label ? `No frozen baseline for ${label}` : 'No previous cycle'}>
        —
      </td>
    );
  }
  return <td className="num">{formatMoney(figures.previous_com)}</td>;
}

function FigureCells({ figures, review, strong }: { figures: ReviewFigures; review: Review; strong?: boolean }) {
  const current = formatMoney(figures.current_com);
  return (
    <>
      <BudgetCell figures={figures} visible={review.budget_visible} />
      <PreviousCell figures={figures} label={review.baseline?.comparison_label ?? null} />
      <td className="num">{strong ? current : <b>{current}</b>}</td>
      <td className="num">
        <span className={deltaClass(figures.delta_to_last_com)}>{formatDelta(figures.delta_to_last_com)}</span>
      </td>
      <td className="num">{formatMoney(figures.p1)}</td>
      <td className="num">{formatMoney(figures.p2)}</td>
      <td className="num">{formatMoney(figures.p3)}</td>
      <td className="num">{formatMoney(figures.bluesky)}</td>
      <td className="num">{formatMoney(figures.unweighted)}</td>
    </>
  );
}

function ClientRow({
  row,
  unit,
  review,
  cycleId,
  onEdit,
}: {
  row: ReviewRow;
  unit: ReviewUnitSection;
  review: Review;
  cycleId: string;
  onEdit: (row: ReviewRow) => void;
}) {
  const risk = riskClass(row.risk_rating?.code);
  const gridLink =
    `/cycles/${encodeURIComponent(cycleId)}/forecast?bu=${encodeURIComponent(unit.business_unit.id)}` +
    `&client=${encodeURIComponent(row.client.id)}`;
  return (
    <tr data-pending={row.pending_lines > 0 || undefined}>
      <td>
        <Link className="cell-link" to={gridLink}>{row.client.name}</Link>
        <span className="sub">
          {unit.business_unit.name}
          {row.pending_lines > 0 && <span className="rv-pending"> · Recalculating…</span>}
        </span>
      </td>
      <td>{row.delivery_managers.join(', ') || '—'}</td>
      <FigureCells figures={row} review={review} />
      <td>{risk && row.risk_rating ? <span className={`badge ${risk}`} title={row.risk_rating.name}>{row.risk_rating.code}</span> : null}</td>
      <td className="num">{formatMoney(row.upside)}</td>
      <td className="num">{formatMoney(row.downside)}</td>
      <td>
        <div className="row-actions">
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            aria-label={`Review attributes for ${row.client.name}`}
            title="Review attributes"
            onClick={() => onEdit(row)}
          >
            <Pencil aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function SubtotalRow({ unit, review }: { unit: ReviewUnitSection; review: Review }) {
  const label = unit.partial ? 'Your clients' : 'Subtotal';
  return (
    <tr className="rv-subtotal">
      <td colSpan={2}>
        <b>{unit.business_unit.name}</b> · {label} · <span className="mono">{unit.business_unit.reporting_currency_code}</span>
      </td>
      <FigureCells figures={unit.subtotal} review={review} strong />
      <td />
      <td className="num">{formatMoney(unit.subtotal.upside)}</td>
      <td className="num">{formatMoney(unit.subtotal.downside)}</td>
      <td />
    </tr>
  );
}

function UnallocatedRow({ unit }: { unit: ReviewUnitSection }) {
  if (!unit.unallocated || unit.unallocated.length === 0) return null;
  return (
    <tr className="rv-unallocated">
      <td colSpan={2}>
        Unallocated
        <span className="sub">Unit budget not yet assigned to a client</span>
      </td>
      <td className="num">
        {unit.unallocated.map((money) => (
          <span key={money.currency_code} className={signOf(money.amount) < 0 ? 'badge pill-danger' : undefined}>
            {formatMoney(money)}
          </span>
        ))}
      </td>
      <td colSpan={12} />
    </tr>
  );
}

function conversionNote(review: Review): string | null {
  const { totals } = review;
  if (totals.conversion_error === 'FX_RATE_MISSING') return 'No USD rate for this cycle';
  if (!totals.conversions.length) return null;
  const notes = totals.conversions.map((c) => {
    const unit = review.units.find((u) => u.business_unit.id === c.business_unit_id);
    const name = unit?.business_unit.name ?? c.source_currency_code;
    return c.fx_rate_applied
      ? `${name} converted at ${c.fx_rate_applied} (cycle ${c.cycle_code})`
      : `${name} awaiting conversion (cycle ${c.cycle_code})`;
  });
  return `${totals.currency_code} · ${notes.join(' · ')}`;
}

function TotalRow({ review }: { review: Review }) {
  const { totals } = review;
  const note = conversionNote(review);
  const count = review.units.length;
  if (!totals.figures) {
    return (
      <tr className="rv-total">
        <td colSpan={15}>
          <b>Total · {count} Business {count === 1 ? 'Unit' : 'Units'}</b>
          {note && <span className="sub">{note}</span>}
        </td>
      </tr>
    );
  }
  return (
    <tr className="rv-total">
      <td colSpan={2}>
        <b>Total · {count} Business {count === 1 ? 'Unit' : 'Units'}</b>
        <span className="sub">
          {note ?? totals.currency_code}
          {totals.conversion_pending && <span className="rv-pending"> · Converting…</span>}
        </span>
      </td>
      <FigureCells figures={totals.figures} review={review} strong />
      <td />
      <td className="num">{formatMoney(totals.figures.upside)}</td>
      <td className="num">{formatMoney(totals.figures.downside)}</td>
      <td />
    </tr>
  );
}

export function ReviewTable({
  review,
  cycleId,
  riskFilter,
  onEdit,
}: {
  review: Review;
  cycleId: string;
  riskFilter: string;
  onEdit: (row: ReviewRow) => void;
}) {
  const label = review.cycle.label;
  const comparison = review.cycle.comparison_label ?? 'Previous COM';
  return (
    <div className="table-scroll">
      <table className="data zebra rv-table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Delivery Manager</th>
            <th className="num">Budget {review.cycle.fiscal_year}</th>
            <th className="num">{comparison}</th>
            <th className="num">{label}</th>
            <th className="num">Delta to last COM</th>
            <th className="num">P1</th>
            <th className="num">P2</th>
            <th className="num">P3</th>
            <th className="num">Bluesky</th>
            <th className="num">Unweighted</th>
            <th>Risk</th>
            <th className="num">Upside</th>
            <th className="num">Downside</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {review.units.map((unit) => {
            const rows = riskFilter ? unit.rows.filter((row) => row.risk_rating?.code === riskFilter) : unit.rows;
            return (
              <Fragment key={unit.business_unit.id}>
                {rows.map((row) => (
                  <ClientRow
                    key={`${row.business_unit_id}:${row.client.id}`}
                    row={row}
                    unit={unit}
                    review={review}
                    cycleId={cycleId}
                    onEdit={onEdit}
                  />
                ))}
                {review.budget_visible && <UnallocatedRow unit={unit} />}
                <SubtotalRow unit={unit} review={review} />
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <TotalRow review={review} />
        </tfoot>
      </table>
    </div>
  );
}
